import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ref, query, orderByChild, limitToLast, get } from 'firebase/database';
import { db } from '../lib/firebase';
import { User } from '../types/user';
import { Building, MapPin, Users } from 'lucide-react';

interface PublicProfile extends User {
  id: string;
}

export default function RecentProfiles() {
  const [profiles, setProfiles] = useState<PublicProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const profilesRef = ref(db, 'vcards');
        const profilesQuery = query(
          profilesRef,
          orderByChild('createdAt'),
          limitToLast(12)
        );

        const snapshot = await get(profilesQuery);
        if (snapshot.exists()) {
          const publicProfiles = Object.entries(snapshot.val())
            .filter(([_, data]) => (data as any).isPublic === true)
            .map(([id, data]) => ({
              ...(data as User),
              id
            }))
            .reverse();

          setProfiles(publicProfiles);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des profils récents:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfiles();
  }, []);

  const displayedProfiles = showAll ? profiles : profiles.slice(0, 6);

  if (loading || profiles.length === 0) {
    return null;
  }

  return (
    <div className="mt-20">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-gray-900">
          Profils publics récents
        </h2>
        <p className="mt-4 text-lg text-gray-600">
          Découvrez les derniers profils professionnels créés sur notre plateforme
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {displayedProfiles.map((profile) => (
          <Link
            key={profile.id}
            to={`/p/${profile.username}`}
            className="block group"
          >
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform duration-200 hover:shadow-lg hover:-translate-y-1">
              <div 
                className="h-24 bg-gradient-to-r"
                style={{
                  background: profile.qrStyle?.fgColor 
                    ? `linear-gradient(135deg, ${profile.qrStyle.fgColor}dd 0%, ${profile.qrStyle.fgColor}99 100%)`
                    : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                }}
              />
              <div className="relative px-6 pb-6">
                <div className="flex justify-center">
                  <div className="-mt-12 w-24 h-24 rounded-full border-4 border-white overflow-hidden bg-gray-100">
                    {profile.logoUrl ? (
                      <img
                        src={profile.logoUrl}
                        alt={`${profile.prenom} ${profile.nom}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
                        <span className="text-2xl font-bold">
                          {profile.prenom?.[0]}{profile.nom?.[0]}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <h3 className="font-semibold text-lg text-gray-900 group-hover:text-primary-600">
                    {profile.prenom} {profile.nom}
                  </h3>
                  {profile.poste && (
                    <p className="text-gray-600 flex items-center justify-center mt-1">
                      <Building className="w-4 h-4 mr-1" />
                      {profile.poste}
                    </p>
                  )}
                  {profile.ville && (
                    <p className="text-gray-500 flex items-center justify-center mt-1 text-sm">
                      <MapPin className="w-4 h-4 mr-1" />
                      {profile.ville}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-10 text-center space-y-4">
        {profiles.length > 6 && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="inline-flex items-center px-6 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            {showAll ? 'Voir moins' : 'Voir plus'}
          </button>
        )}
        <div>
          <Link
            to="/all-profiles"
            className="inline-flex items-center px-6 py-3 text-base font-medium rounded-md text-primary-600 bg-primary-25 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Users className="w-5 h-5 mr-2" />
            Explorer tous les profils
          </Link>
        </div>
      </div>
    </div>
  );
}
